import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';

import { ProviderFeedsBase } from '../shared/provider-feeds-base';
import { GtfsProviderShellComponent } from './gtfs-provider-shell.component';
import { ProviderConfig } from '../models/provider.model';
import { Grid } from '../../../shared/components/grid/grid';
import { GridPage } from '../../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../../shared/components/grid/directives/grid-cell.directive';
import { GridColumn, gridColumns } from '../../../shared/components/grid/models/grid.model';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { GtfsDiagnosticsPayload } from '../../../shared/models/feed-diagnostics.model';
import { relativeTime } from '../../../shared/utils/relative-time';
import { isSnapshotStale } from '../util/feed-diagnostics.util';
import { asGtfsDiagnostics, gtfsStatusDot } from '../util/gtfs-diagnostics.util';
import { gtfsFormFromConfiguration, gtfsRealtimeEnabled } from '../util/gtfs-config';

/** GTFS Feeds tab (/admin/providers/gtfs/:providerId): Feed, Tenant, a
 *  Realtime / Schedule only column (whether a TripUpdates URL is configured),
 *  Status (the feed configuration's verdict) and Health (GTFS's own status from
 *  the latest diagnostics snapshot, joined from the shell's one admin call). */
@Component({
  selector: 'app-gtfs-provider-feeds',
  standalone: true,
  imports: [Grid, GridPage, LabelBadge, GridCellDirective, TranslocoDirective],
  templateUrl: './gtfs-provider-feeds.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full min-h-0' },
})
export class GtfsProviderFeedsComponent extends ProviderFeedsBase {
  private readonly gtfsShell = inject(GtfsProviderShellComponent);

  readonly isDiagnosticsLoading = this.gtfsShell.isDiagnosticsLoading;

  /** The feed's latest GTFS diagnostics, or null if it has no snapshot. */
  health(feed: ProviderConfig): GtfsDiagnosticsPayload | null {
    return asGtfsDiagnostics(this.gtfsShell.diagnostics().get(feed.id));
  }

  healthDot(health: GtfsDiagnosticsPayload): LabelBadgeDot {
    return gtfsStatusDot(health.status);
  }

  /** "2m ago", flagged when the snapshot is older than one poll cycle. */
  snapshotAge(feed: ProviderConfig): { age: string; stale: boolean } | null {
    const snapshot = this.gtfsShell.diagnostics().get(feed.id);
    return snapshot ? { age: relativeTime(snapshot.generatedAt), stale: isSnapshotStale(snapshot) } : null;
  }

  isRealtime(feed: ProviderConfig): boolean {
    return gtfsRealtimeEnabled(gtfsFormFromConfiguration(feed.configuration));
  }

  readonly columns = computed<GridColumn<ProviderConfig>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.feedsGrid.columns.${k}`);
    return gridColumns<ProviderConfig>([
      ...this.leadingColumns(),
      { key: 'realtime', label: t('realtime'), type: 'template', template: 'realtime', width: 170, sortable: false },
      this.statusColumn(),
      { key: 'health', label: t('health'), type: 'template', template: 'health', width: 220, sortable: false },
    ]);
  });
}
